import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  scheduleOpsProgramMeeting,
  TOpsProgramDetailEx,
  TOpsMeetingKind,
  TOpsMeetingResult,
} from '../../../requests/opsRegistrationsExtra';
import { btnGhost, btnPrimary, Card, inputClass, labelClass } from '../ui';
import { downloadMeetingIcs as downloadIcs } from './shared';

/** Schedule (or reschedule) the kickoff or the wrap, then hand out the invite. */
const MeetingCard: React.FC<{
  kind: TOpsMeetingKind;
  programId: string;
  program: TOpsProgramDetailEx;
  onClose: () => void;
  openBrief: () => void;
}> = ({ kind, programId, program, onClose, openBrief }) => {
  const queryClient = useQueryClient();
  const meetingKind = kind;
  // Opening the form (again) starts it from what is saved on the program
  const [meeting, setMeeting] = useState(() => {
    const existing = program?.meetings?.[kind];
    const toLocal = (iso: string) => {
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    return {
      start: existing ? toLocal(existing.start) : '',
      end: existing ? toLocal(existing.end) : '',
      timeZone: existing?.timeZone ?? 'PST',
      attendees: [
        ...(existing?.attendees ?? []),
        ...(!existing && program?.contractPoc?.email
          ? [program.contractPoc.email]
          : []),
      ].join(', '),
    };
  });
  const [meetingResult, setMeetingResult] = useState<TOpsMeetingResult | null>(
    null,
  );

  const scheduleMeeting = useMutation({
    mutationFn: () =>
      scheduleOpsProgramMeeting(programId, meetingKind!, {
        start: new Date(meeting.start).toISOString(),
        end: new Date(meeting.end).toISOString(),
        timeZone: meeting.timeZone || undefined,
        attendees: meeting.attendees
          .split(/[,;\n]/)
          .map((a) => a.trim())
          .filter(Boolean),
      }),
    onSuccess: (r) => {
      showToast(
        `${r.kind === 'kickoff' ? 'Kickoff' : 'Wrap'} scheduled`,
        'success',
      );
      setMeetingResult(r);
      queryClient.invalidateQueries({ queryKey: ['opsProgram', programId] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const downloadMeetingIcs = (k: TOpsMeetingKind) =>
    downloadIcs(programId, k, program.name);

  return (
    <Card
      className="mb-4"
      title={
        meetingKind === 'kickoff'
          ? 'Schedule the kickoff with the POC'
          : 'Schedule the wrap / debrief'
      }
      right={
        <button className={btnGhost} onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="flex flex-wrap items-end gap-3 px-5 py-4">
        <div>
          <div className={labelClass}>Start</div>
          <input
            type="datetime-local"
            className={inputClass}
            value={meeting.start}
            onChange={(e) => setMeeting({ ...meeting, start: e.target.value })}
          />
        </div>
        <div>
          <div className={labelClass}>End</div>
          <input
            type="datetime-local"
            className={inputClass}
            value={meeting.end}
            onChange={(e) => setMeeting({ ...meeting, end: e.target.value })}
          />
        </div>
        <div>
          <div className={labelClass}>Time zone label</div>
          <select
            className={inputClass}
            value={meeting.timeZone}
            onChange={(e) =>
              setMeeting({ ...meeting, timeZone: e.target.value })
            }
          >
            <option value="PST">PST</option>
            <option value="EST">EST</option>
            <option value="">None</option>
          </select>
        </div>
        <div className="min-w-[280px] flex-1">
          <div className={labelClass}>Attendees (emails, comma separated)</div>
          <input
            className={`${inputClass} w-full`}
            value={meeting.attendees}
            onChange={(e) =>
              setMeeting({ ...meeting, attendees: e.target.value })
            }
          />
        </div>
        <button
          className={btnPrimary}
          disabled={scheduleMeeting.isPending || !meeting.start || !meeting.end}
          onClick={() => scheduleMeeting.mutate()}
        >
          {scheduleMeeting.isPending
            ? 'Saving…'
            : 'Mark Scheduled and build the invite'}
        </button>
        <div className="basis-full text-xs text-gray-bold">
          Sets{' '}
          {meetingKind === 'kickoff'
            ? 'Primary POC Kick Off Meeting'
            : 'Program Wrap / Debrief'}{' '}
          to Scheduled on the program and keeps the slot here; the calendar file
          is built from it. Times are entered in your browser's clock.
        </div>
        {meetingResult && (
          <div className="basis-full rounded-md bg-green-500 px-4 py-2.5 text-sm text-green-900">
            Scheduled.{' '}
            <button
              className="underline"
              onClick={() => downloadMeetingIcs(meetingResult.kind)}
            >
              Download the .ics
            </button>{' '}
            ·{' '}
            <button
              className="underline"
              onClick={() => {
                navigator.clipboard
                  ?.writeText(meetingResult.icsLink)
                  .then(() => showToast('Invite link copied', 'success'));
              }}
            >
              Copy the signed invite link for the email
            </button>{' '}
            ·{' '}
            <button className="underline" onClick={openBrief}>
              Open the brief
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MeetingCard;
